import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Loader2, ShieldCheck, FileCheck, ScrollText, CheckCircle2, AlertCircle, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import SmartUploader from '../components/SmartUploader';

const STEPS = [
  { id: 1, label: 'Documents', icon: FileCheck },
  { id: 2, label: 'Confirmations', icon: ScrollText },
  { id: 3, label: 'Terminé', icon: CheckCircle2 },
];

const CandidateOnboardingPage = () => {
  const { token } = useParams();
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);

  // Form state for step 2
  const [phone, setPhone] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [availability, setAvailability] = useState('');
  const [fullName, setFullName] = useState('');
  const [acceptedClauses, setAcceptedClauses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const reload = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/onboarding/${token}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Lien invalide');
        if (res.status === 410) throw new Error('Lien expiré');
        throw new Error(`HTTP ${res.status}`);
      }
      const body = await res.json();
      setData(body);
      // pre-fill from candidate
      if (!phone) setPhone(body.candidate?.phone || '');
      if (!emailValue) setEmailValue(body.candidate?.email || '');
      if (!availability) setAvailability(body.candidate?.availability || '');
      if (!fullName) {
        setFullName(`${body.candidate?.first_name || ''} ${body.candidate?.last_name || ''}`.trim());
      }
      // jump to step 3 if already complete
      if (body.session?.status === 'completed') setStep(3);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const cfgRes = await fetch(`${apiUrl}/api/onboarding/config`);
        const cfgBody = await cfgRes.json();
        setConfig(cfgBody);
        await reload();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onUploaded = () => reload();

  const submitStep2 = async () => {
    // Validation
    if (!fullName.trim()) {
      toast.error('Renseigne ton nom complet');
      return;
    }
    if (!availability.trim()) {
      toast.error('Indique ta disponibilité');
      return;
    }
    const requiredKeys = (config?.clauses || []).map((c) => c.key);
    const allChecked = requiredKeys.every((k) => acceptedClauses[k]);
    if (!allChecked) {
      toast.error('Tu dois cocher toutes les clauses pour continuer');
      return;
    }

    setSubmitting(true);
    try {
      // Update contact info
      await fetch(`${apiUrl}/api/onboarding/${token}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailValue || undefined,
          phone: phone || undefined,
          availability: availability || undefined,
        }),
      });

      // Sign
      const signRes = await fetch(`${apiUrl}/api/onboarding/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: fullName,
          accepted_clauses: requiredKeys,
        }),
      });
      if (!signRes.ok) {
        const err = await signRes.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur signature ${signRes.status}`);
      }

      // Complete
      const compRes = await fetch(`${apiUrl}/api/onboarding/${token}/complete`, {
        method: 'POST',
      });
      if (!compRes.ok) {
        const err = await compRes.json().catch(() => ({}));
        const detail = typeof err.detail === 'string' ? err.detail : err.detail?.error;
        const map = {
          missing_signature: 'Signature manquante',
          missing_id_document: 'Document d\'identité requis (étape 1)',
          missing_availability: 'Disponibilité requise',
        };
        throw new Error(map[detail] || detail || 'Erreur finalisation');
      }

      toast.success('Onboarding terminé');
      setStep(3);
      reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-tp-bg/40 to-white">
        <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-white p-6">
        <Card className="max-w-md p-6 text-center border-red-200">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-red-900 mb-2">Onboarding indisponible</h1>
          <p className="text-sm text-red-700">{error}</p>
        </Card>
      </div>
    );
  }

  const docs = data?.session?.documents || {};
  const hasIdDoc = (docs.id_document || []).length > 0;
  const uploadUrl = `${apiUrl}/api/onboarding/${token}/upload`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-tp-bg/40 to-white pb-12" data-testid="candidate-onboarding-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 pt-6 md:pt-10">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-4 w-4 text-tp-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-primary">
              Talent Partner · onboarding sécurisé
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text leading-tight">
            Bienvenue {data.candidate.first_name || ''} 👋
          </h1>
          <p className="text-sm text-tp-text-light mt-1">
            Quelques minutes pour finaliser ton profil et passer à l'étape "présentable client".
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-6 px-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.id;
            const done = step > s.id;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${
                    done ? 'bg-emerald-600 text-white' : active ? 'bg-tp-primary text-white' : 'bg-tp-border text-tp-text-light'
                  }`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3.5 w-3.5" />}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide ${active ? 'text-tp-primary font-semibold' : 'text-tp-text-light'}`}>
                    {s.label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${step > s.id ? 'bg-emerald-600' : 'bg-tp-border'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* STEP 1 - Documents */}
        {step === 1 && (
          <Card className="p-4 md:p-6 space-y-5" data-testid="onboarding-step-1">
            <div>
              <h2 className="text-lg font-semibold mb-1">Étape 1 — Documents</h2>
              <p className="text-xs text-tp-text-light">
                Le document d'identité est obligatoire. Les autres sont fortement recommandés pour rassurer les recruteurs.
              </p>
            </div>

            {(config?.document_types || []).map((dt) => (
              <SmartUploader
                key={dt.key}
                uploadUrl={uploadUrl}
                docType={dt.key}
                label={dt.label}
                helperText={dt.required ? 'Obligatoire' : 'Recommandé'}
                multiple={!!dt.multiple}
                required={!!dt.required}
                currentFiles={docs[dt.key] || []}
                onUploaded={onUploaded}
                testIdPrefix={`doc-${dt.key}-`}
              />
            ))}

            <div className="flex justify-end pt-2 border-t border-tp-border">
              <Button
                onClick={() => setStep(2)}
                disabled={!hasIdDoc}
                className="bg-tp-primary hover:bg-tp-primary-light text-white"
                data-testid="onboarding-next-step-1"
              >
                Continuer <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {!hasIdDoc && (
              <p className="text-xs text-orange-700 text-right">
                Ajoute ton document d'identité pour continuer.
              </p>
            )}
          </Card>
        )}

        {/* STEP 2 - Signature & Confirmations */}
        {step === 2 && (
          <Card className="p-4 md:p-6 space-y-5" data-testid="onboarding-step-2">
            <div>
              <h2 className="text-lg font-semibold mb-1">Étape 2 — Confirmations</h2>
              <p className="text-xs text-tp-text-light">
                Vérifie tes coordonnées et accepte les conditions.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-tp-text">Email</label>
                <Input value={emailValue} onChange={(e) => setEmailValue(e.target.value)} type="email" data-testid="ob-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-tp-text">Téléphone</label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="ob-phone" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-tp-text">Disponibilité <span className="text-red-500">*</span></label>
                <Input
                  value={availability}
                  onChange={(e) => setAvailability(e.target.value)}
                  placeholder="Ex: immédiate, dans 2 semaines, juin 2026..."
                  data-testid="ob-availability"
                />
              </div>
            </div>

            <div className="border-t border-tp-border pt-4 space-y-3">
              <p className="text-sm font-semibold text-tp-text">Conditions à accepter</p>
              {(config?.clauses || []).map((c) => (
                <label key={c.key} className="flex items-start gap-2.5 cursor-pointer" data-testid={`clause-${c.key}`}>
                  <Checkbox
                    checked={!!acceptedClauses[c.key]}
                    onCheckedChange={(checked) => setAcceptedClauses((p) => ({ ...p, [c.key]: !!checked }))}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-tp-text leading-snug">
                    <span className="font-medium">{c.label} — </span>{c.text}
                  </span>
                </label>
              ))}
            </div>

            <div className="border-t border-tp-border pt-4">
              <label className="text-xs font-medium text-tp-text">Signature (nom complet) <span className="text-red-500">*</span></label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ton nom complet"
                data-testid="ob-fullname"
              />
              <p className="text-[10px] text-tp-text-light mt-1 italic">
                En signant, tu confirmes accepter toutes les clauses ci-dessus. Date, IP et version du texte sont enregistrées.
              </p>
            </div>

            <div className="flex justify-between gap-2 pt-2 border-t border-tp-border">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="onboarding-back-step-2">
                ← Retour
              </Button>
              <Button
                onClick={submitStep2}
                disabled={submitting}
                className="bg-tp-primary hover:bg-tp-primary-light text-white"
                data-testid="onboarding-submit-step-2"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Signer et finaliser
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 3 - Done */}
        {step === 3 && (
          <Card className="p-6 md:p-8 text-center border-emerald-200 bg-emerald-50/40" data-testid="onboarding-step-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-xl md:text-2xl font-display font-semibold text-emerald-900 mb-2">
              Onboarding terminé 🎉
            </h2>
            <p className="text-sm text-emerald-800 max-w-md mx-auto">
              Ton profil est maintenant présentable aux recruteurs. Tu seras contacté dès qu'une mission correspond.
            </p>
            <p className="text-xs text-emerald-700 mt-4 italic">
              Tu peux fermer cette page.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CandidateOnboardingPage;
